import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Alert
} from '@mui/material';
import { Description, EventAvailable } from '@mui/icons-material';
import { bookingTermsService } from '../services/firebase';
import { BookingTermsData } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';
import ReactMarkdown from 'react-markdown';

const BookingTermsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [termsData, setTermsData] = useState<BookingTermsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTermsData = async () => {
      try {
        setLoading(true);
        let data = await bookingTermsService.getBookingTermsData();
        
        if (!data) {
          // Initialize with default data if no data exists
          await bookingTermsService.initializeBookingTermsData();
          data = await bookingTermsService.getBookingTermsData();
        }
        
        setTermsData(data);
      } catch (err: any) {
        setError(t('booking_terms_page.errors.load', { message: err.message }));
      } finally {
        setLoading(false);
      }
    };

    fetchTermsData();
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const formattedDate = termsData?.lastUpdated 
    ? new Date(termsData.lastUpdated.toDate()).toLocaleDateString(i18n.language, { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    : t('common.notSpecified');

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(termsData?.title, i18n.language) || t('static_pages.booking_terms')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(termsData?.description, i18n.language) || t('static_pages.booking_terms_description')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh',
        py: 8
      }}>
        <Container maxWidth="lg">
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'var(--light-blue-cta)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 8px 24px rgba(0,91,189,0.2)'
                }}>
                  <Description sx={{ fontSize: 50 }} />
                </Box>
              </Box>
              
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '3rem', md: '4rem' },
                  color: 'var(--navy-blue)',
                  mb: 2
                }}
              >
                {getTranslatedData(termsData?.title, i18n.language) || t('static_pages.booking_terms')}
              </Typography>
              
              <Typography 
                variant="h5" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  mb: 4
                }}
              >
                {getTranslatedData(termsData?.description, i18n.language) || t('static_pages.booking_terms_description')}
              </Typography>

              <Chip
                icon={<EventAvailable />}
                label={`${t('static_pages.last_updated')}: ${formattedDate}`}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  fontWeight: 500,
                  fontSize: '1rem',
                  p: 2.5,
                  border: '1px solid var(--gray-shadow)'
                }}
              />
            </Box>

            {/* Terms Content */}
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 3, sm: 4, md: 6 },
                backgroundColor: 'white',
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
              }}
            >
              <CardContent>
                <Box 
                  className="markdown-content"
                  sx={{ 
                    color: 'var(--navy-blue)', 
                    lineHeight: 1.8, 
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    '& h2': {
                      fontWeight: 600,
                      fontSize: '1.5rem',
                      color: 'var(--light-blue-cta)',
                      mt: 4,
                      mb: 2,
                      borderBottom: '2px solid var(--light-gray)',
                      pb: 1,
                    },
                    '& h3': {
                      fontWeight: 600,
                      fontSize: '1.3rem',
                      color: 'var(--navy-blue)',
                      mt: 3,
                      mb: 1.5,
                    },
                    '& p': {
                      mb: 2
                    },
                    '& ul': {
                      pl: 3,
                      mb: 2
                    },
                    '& li': {
                      mb: 1
                    },
                    '& strong': {
                      fontWeight: 600,
                      color: 'var(--light-blue-cta)'
                    }
                  }}
                >
                  <ReactMarkdown>
                    {getTranslatedData(termsData?.content, i18n.language)}
                  </ReactMarkdown>
                </Box>
              </CardContent>
            </Paper>
        </Container>
      </Box>
    </>
  );
};

export default BookingTermsPage; 